import { c as _c } from "react/compiler-runtime";
import { postHostedOnboardingSkipPasskeyRegisterOptions } from '@onefootprint/fetch-hosted';
import type { ActionKind } from '@onefootprint/request-types';
import { createFileRoute } from '@tanstack/react-router';
import z from 'zod';
import RegisterPasskey from '@/idv2/features/liveness/register-passkey';
import getSkipReason from '@/idv2/features/liveness/utils/get-skip-reason';
import { useFootprint } from '@/idv2/hooks/use-footprint';
import checkIsInIframe from '@/idv2/utils/check-is-in-iframe';
import getDevice from '@/idv2/utils/get-device';
import { redirectToCheckRequirements } from '@/idv2/utils/loader-navigation';
import { getLogger } from '@/idv2/utils/logger';
const {
  logInfo
} = getLogger({
  name: 'idv2',
  location: 'liveness-route'
});
const actionKindValues: Record<ActionKind, ActionKind> = {
  replace: 'replace',
  add_primary: 'add_primary'
};
const actionKindSchema = z.enum(actionKindValues);
const livenessSearchSchema = z.object({
  actionKind: actionKindSchema
});
export const Route = createFileRoute('/onboarding/$token/liveness')({
  validateSearch: livenessSearchSchema,
  component: LivenessPage,
  beforeLoad: async ({
    context: {
      currentRequirement,
      queryClient
    },
    params: {
      token
    }
  }) => {
    if (currentRequirement?.kind !== 'liveness') {
      throw new Error(`Liveness loader invalid requirement kind: ${currentRequirement?.kind ?? 'unknown'}`);
    }
    logInfo('Liveness beforeLoad started', {
      requirementKind: currentRequirement.kind
    });
    const deviceInfo = await getDevice();
    const isInIframe = checkIsInIframe();
    if (!deviceInfo?.hasSupportForWebauthn || isInIframe) {
      const reason = getSkipReason({
        deviceInfo,
        isInIframe,
        isUnavailable: true
      });
      logInfo(`Skipping passkeys: ${reason}`);
      await queryClient.fetchQuery({
        ...postHostedOnboardingSkipPasskeyRegisterOptions({
          headers: {
            'X-Fp-Authorization': token
          },
          body: {
            context: {
              reason,
              clientType: 'web',
              numAttempts: 0,
              attempts: []
            }
          }
        }),
        staleTime: Infinity
      });
      return redirectToCheckRequirements(queryClient, token);
    }
    return {
      deviceInfo,
      isInIframe
    };
  },
  loader: async ({
    context: {
      deviceInfo,
      isInIframe
    }
  }) => {
    logInfo('Liveness register passkey loader started');
    return {
      deviceInfo,
      isInIframe
    };
  }
});
function LivenessPage() {
  const $ = _c(6);
  const {
    deviceInfo,
    isInIframe
  } = Route.useLoaderData();
  const {
    actionKind
  } = Route.useSearch();
  const {
    token
  } = Route.useParams();
  const {
    navigation
  } = useFootprint();
  let t0;
  if ($[0] !== actionKind || $[1] !== deviceInfo || $[2] !== isInIframe || $[3] !== navigation.forward || $[4] !== token) {
    t0 = <RegisterPasskey deviceInfo={deviceInfo} isInIframe={isInIframe} token={token} actionKind={actionKind} onComplete={navigation.forward} />;
    $[0] = actionKind;
    $[1] = deviceInfo;
    $[2] = isInIframe;
    $[3] = navigation.forward;
    $[4] = token;
    $[5] = t0;
  } else {
    t0 = $[5];
  }
  return t0;
}